// components/layout/NavMenu/NavMenuV2.tsx
//
// Redesigned navigation shell (Phase 3) — same architecture as the classic
// NavMenu (fixed top bar + temporary left drawer), restyled to the locked
// design language. Reuses ALL of the classic nav's hooks, so permissions,
// badge counts, break toggle, logout and section-expand state are identical —
// only the presentation changes.
//
// Shipped behind the uiTheme 'preview' flag; PageContainer renders this for
// preview users and the classic NavMenu otherwise.
//
// Deferred (fast-follow): an always-visible collapsed rail. That needs a
// persistent sidebar + per-page content offset (app-wide layout change), so
// it's intentionally not part of this drawer-based first cut.

import { ReactElement, useMemo, useState, MouseEvent } from 'react';
import { WhatsNewDialog } from '../../../features/landing/components/WhatsNewDialog';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, Drawer, Typography, Chip, IconButton, Tooltip,
  Collapse, Menu, MenuItem, Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BoltIcon from '@mui/icons-material/Bolt';
import WorkIcon from '@mui/icons-material/Work';
import TableChartIcon from '@mui/icons-material/TableChart';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import TimerIcon from '@mui/icons-material/Timer';
import BugReportIcon from '@mui/icons-material/BugReport';
import GavelIcon from '@mui/icons-material/Gavel';
import WarningIcon from '@mui/icons-material/Warning';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ArchiveIcon from '@mui/icons-material/Archive';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BadgeIcon from '@mui/icons-material/Badge';
import VideocamIcon from '@mui/icons-material/Videocam';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import CampaignIcon from '@mui/icons-material/Campaign';
import ExploreIcon from '@mui/icons-material/Explore';

import { useAuth } from '../../../context/AuthContext';
import { useTournament } from '../../../context/TournamentContext';
import { useTournamentRole } from '../../../hooks/useTournamentRole';
import { useStoreManagerships } from '../../../hooks/useStoreManagerships';
import { ROUTES, routes } from '../../../config/routes';
import type { UserData } from '../../../context/AuthContext';
import { CustomLink, NotificationBadge } from './NavDrawerComponents';
import { useNavPermissions, useNavSubscriptions, useNavBadgeCounts, useNavActions } from './hooks';
import { useTour, TOURS_ENABLED } from '../../../features/tour';

export interface NavMenuV2Props {
  user: UserData | null;
}

const TONE = {
  primary: '#00FFD4', success: '#00FF88', warning: '#FFE600', info: '#00D4FF',
  error: '#FF3366', secondary: '#7B68EE', orange: '#FF6B35', neutral: '#8892A0',
} as const;
type Tone = keyof typeof TONE;

export function NavMenuV2({ user }: NavMenuV2Props): ReactElement {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { currentTournamentId } = useTournament();

  const userFirstTournamentId = useMemo(() => {
    if (user?.tournamentAssignments) {
      const ids = Object.keys(user.tournamentAssignments);
      if (ids.length > 0) return ids[0];
    }
    if (user?.floorTeamAssignments) {
      const ids = Object.keys(user.floorTeamAssignments);
      if (ids.length > 0) return ids[0];
    }
    return null;
  }, [user?.tournamentAssignments, user?.floorTeamAssignments]);

  const navTournamentId = currentTournamentId || userFirstTournamentId;
  const subscriptions = useNavSubscriptions({ user });
  const { tournamentRole } = useTournamentRole(user, navTournamentId);
  const storeMgr = useStoreManagerships();
  const permissions = useNavPermissions({
    user,
    isActingLeadFromTeams: subscriptions.isActingLeadFromTeams,
    tournamentRole,
    storeMgrCanCreateTournaments: storeMgr.canCreateTournaments,
    storeMgrCanManageSeries: storeMgr.canManageSeries,
  });
  const badges = useNavBadgeCounts({ user, permissions, tournaments: subscriptions.tournaments });
  const actions = useNavActions({ user, onBreak: subscriptions.onBreak });

  const { tournaments, engineTournaments, onBreak } = subscriptions;
  const { deckCheckTournaments, pendingAppeals, pendingPenalties, pendingUsers } = badges;
  const {
    drawerOpen, toggleDrawer,
    tournamentExpanded, setTournamentExpanded,
    appealsExpanded, setAppealsExpanded,
    managementExpanded, setManagementExpanded,
    toggleBreak,
  } = actions;

  const [roundsAnchor, setRoundsAnchor] = useState<null | HTMLElement>(null);
  const [adminExpanded, setAdminExpanded] = useState(true);
  const [whatsNewReq, setWhatsNewReq] = useState(0);
  const { start: startTour } = useTour();
  const handleLogout = async () => { await logout(); navigate(ROUTES.LOGIN); };
  const goHome = () => { if (drawerOpen) toggleDrawer(); navigate('/landing'); };

  const appealsPending = pendingAppeals + pendingPenalties;

  // -- presentational row -----------------------------------------------------
  const row = (opts: {
    to?: string; onClick?: (e: MouseEvent<HTMLElement>) => void;
    icon: ReactElement; label: string; tone: Tone; count?: number | null; badge?: number;
  }): ReactElement => {
    const color = TONE[opts.tone];
    const inner = (
      <>
        <Box sx={{ display: 'flex', color, minWidth: 24 }}>
          {opts.badge ? <NotificationBadge count={opts.badge}>{opts.icon}</NotificationBadge> : opts.icon}
        </Box>
        <Typography noWrap sx={{ flex: 1, fontSize: '0.88rem', fontWeight: 500, color: 'text.primary' }}>{opts.label}</Typography>
        {opts.count ? <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{opts.count}</Typography> : null}
      </>
    );
    const sx = {
      display: 'flex', alignItems: 'center', gap: 1.5, mx: 1, px: 1.25, height: 42,
      borderRadius: 2, cursor: 'pointer', textDecoration: 'none',
      '&:hover': { backgroundColor: alpha('#FFFFFF', 0.05) },
    } as const;
    if (opts.to) {
      return <Box key={opts.label} component={CustomLink} to={opts.to} onClick={toggleDrawer} sx={sx}>{inner}</Box>;
    }
    return <Box key={opts.label} onClick={opts.onClick} sx={sx}>{inner}</Box>;
  };

  const sectionHeader = (title: string, expanded: boolean, onToggle: () => void, pending = 0) => (
    <Box onClick={onToggle} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, pt: 1.5, pb: 0.5, cursor: 'pointer' }}>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.66rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase' }}>{title}</Typography>
      {pending > 0 && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'warning.main' }} />}
      <ExpandMoreIcon sx={{ ml: 'auto', fontSize: 18, color: 'text.disabled', transform: expanded ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }} />
    </Box>
  );

  const footItem = (label: string, icon: ReactElement, color: string, onClick: () => void, tint = false) => (
    <Box onClick={onClick} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mx: 1, px: 1.25, height: 42, borderRadius: 2, cursor: 'pointer', border: tint ? '1px solid' : 'none', borderColor: tint ? alpha(color, 0.3) : 'transparent', backgroundColor: tint ? alpha(color, 0.1) : 'transparent', '&:hover': { backgroundColor: alpha('#FFFFFF', 0.05) } }}>
      <Box sx={{ display: 'flex', color }}>{icon}</Box>
      <Typography noWrap sx={{ fontSize: '0.88rem', fontWeight: 600, color: tint ? color : 'text.primary' }}>{label}</Typography>
    </Box>
  );

  return (
    <>
      {/* Locked top bar */}
      <AppBar position="fixed" elevation={0} sx={{ background: alpha('#0C1118', 0.92), backdropFilter: 'blur(8px)', borderBottom: '1px solid', borderColor: alpha('#FFFFFF', 0.08) }}>
        <Toolbar sx={{ gap: { xs: 1, sm: 1.5 }, minHeight: 56, px: { xs: 1, sm: 2 } }}>
          <IconButton onClick={toggleDrawer} aria-label="Open navigation menu" sx={{ color: 'primary.main' }}><MenuIcon /></IconButton>
          {/* Brand → dashboard */}
          <Typography onClick={goHome} sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.05rem' }, cursor: 'pointer', background: (t) => `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.info.main})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>RoundMaster</Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={onBreak ? 'On break — click to go On Duty' : 'On duty — click to take a break'}>
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
              label={onBreak ? 'On Break' : 'On Duty'}
              size="small"
              onClick={toggleBreak}
              sx={{ fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer', backgroundColor: (t) => alpha(onBreak ? t.palette.warning.main : t.palette.success.main, 0.15), color: onBreak ? 'warning.main' : 'success.main', '& .MuiChip-icon': { color: onBreak ? 'warning.main' : 'success.main' } }}
            />
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 300 }, maxWidth: '100vw', background: 'linear-gradient(180deg, #11161F 0%, #0C1118 100%)', borderRight: '1px solid', borderColor: alpha('#FFFFFF', 0.08), display: 'flex', flexDirection: 'column' } }}
      >
        {/* Drawer header */}
        <Box sx={{ display: 'flex', alignItems: 'center', height: 56, px: 2, flexShrink: 0, borderBottom: '1px solid', borderColor: alpha('#FFFFFF', 0.06) }}>
          <Typography onClick={goHome} sx={{ fontWeight: 800, cursor: 'pointer', background: (t) => `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.info.main})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>RoundMaster</Typography>
          <IconButton onClick={toggleDrawer} aria-label="Close menu" sx={{ ml: 'auto', color: 'text.secondary' }}><CloseIcon /></IconButton>
        </Box>

        {/* Scrollable items */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
          {/* Main */}
          {row({ to: '/landing', icon: <DashboardIcon />, label: 'Dashboard', tone: 'primary' })}
          {permissions.canSetupTournament && row({ to: '/tournament-setup', icon: <AddCircleOutlineIcon />, label: 'Create Tournament', tone: 'success' })}
          {permissions.canViewTournamentEngine && tournaments.length > 0 && row({ to: ROUTES.TOURNAMENT_DASHBOARD, icon: <EmojiEventsIcon />, label: 'Tournament Dashboard', tone: 'warning', count: engineTournaments.length || null })}
          {permissions.canViewMatchmaking && row({ to: ROUTES.MATCHMAKING_DASHBOARD, icon: <BoltIcon />, label: 'Matchmaking', tone: 'info' })}
          {permissions.canViewJobBoard && row({ to: ROUTES.JOB_BOARD, icon: <WorkIcon />, label: 'Job Board', tone: 'info' })}

          <Divider sx={{ my: 1, borderColor: alpha('#FFFFFF', 0.06) }} />

          {/* Tournament Tools */}
          {sectionHeader('Tournament Tools', tournamentExpanded, () => setTournamentExpanded(!tournamentExpanded))}
          <Collapse in={tournamentExpanded} timeout="auto">
            {row({ to: ROUTES.TABLE_MANAGEMENT_DASHBOARD, icon: <TableChartIcon />, label: 'Table Management', tone: 'primary', count: tournaments.length || null })}
            {permissions.canViewDeckCheck && row({ to: ROUTES.DECK_CHECK_DASHBOARD, icon: <FactCheckIcon />, label: 'Deck Check', tone: 'secondary', count: deckCheckTournaments.length || null })}
            {row({ to: ROUTES.PLAYERS_DASHBOARD, icon: <PeopleIcon />, label: 'Players', tone: 'info', count: tournaments.length || null })}
            {permissions.canViewTimer && row({ to: '/timer-overview', icon: <TimerIcon />, label: 'Timer Overview', tone: 'primary' })}
            {row({ onClick: (e) => setRoundsAnchor(e.currentTarget), icon: <HistoryIcon />, label: 'Round Archive', tone: 'warning' })}
            {permissions.canViewProblemTracker && row({ to: ROUTES.PROBLEM_TRACKER_DASHBOARD, icon: <BugReportIcon />, label: 'Problem Tracker', tone: 'orange', count: tournaments.length || null })}
          </Collapse>
          <Menu anchorEl={roundsAnchor} open={Boolean(roundsAnchor)} onClose={() => setRoundsAnchor(null)} PaperProps={{ sx: { backgroundColor: '#1E2530', border: '1px solid', borderColor: alpha('#FFFFFF', 0.1) } }}>
            {tournaments.map((t) => (
              <MenuItem key={t.id} component={CustomLink} to={routes.roundArchive(t.id)} onClick={() => { setRoundsAnchor(null); toggleDrawer(); }} sx={{ color: 'text.primary' }}>{t.name}</MenuItem>
            ))}
            {tournaments.length === 0 && <MenuItem sx={{ color: 'text.secondary' }}>No tournaments</MenuItem>}
          </Menu>

          {/* Appeals & Staff */}
          {(permissions.canViewAppeals || permissions.canApprovePenalties || permissions.canViewStaffSettings) && (
            <>
              <Divider sx={{ my: 1, borderColor: alpha('#FFFFFF', 0.06) }} />
              {sectionHeader('Appeals & Staff', appealsExpanded, () => setAppealsExpanded(!appealsExpanded), appealsPending)}
              <Collapse in={appealsExpanded} timeout="auto">
                {permissions.canViewAppeals && row({ to: '/appeals', icon: <GavelIcon />, label: 'Appeals', tone: 'warning', badge: pendingAppeals })}
                {permissions.canViewAppeals && row({ to: '/appeals-log', icon: <HistoryIcon />, label: 'Appeals Log', tone: 'warning' })}
                {permissions.canApprovePenalties && row({ to: '/penalty-approval', icon: <WarningIcon />, label: 'Penalty Approval', tone: 'error', badge: pendingPenalties })}
                {permissions.canViewStaffSettings && !permissions.canManage && row({ to: ROUTES.STAFF_DASHBOARD, icon: <ManageAccountsIcon />, label: 'Staff Dashboard', tone: 'secondary' })}
                {permissions.canViewStaffSettings && row({ to: ROUTES.STAFF_FEEDBACK, icon: <RateReviewIcon />, label: 'Staff Feedback', tone: 'info' })}
              </Collapse>
            </>
          )}

          {/* Management */}
          {permissions.canManage && (
            <>
              <Divider sx={{ my: 1, borderColor: alpha('#FFFFFF', 0.06) }} />
              {sectionHeader('Management', managementExpanded, () => setManagementExpanded(!managementExpanded), permissions.isStoreOwner ? 0 : pendingUsers)}
              <Collapse in={managementExpanded} timeout="auto">
                {row({ to: ROUTES.STAFF_DASHBOARD, icon: <ManageAccountsIcon />, label: 'Staff Dashboard', tone: 'secondary', badge: permissions.isStoreOwner ? 0 : pendingUsers })}
                {permissions.canManage && row({ to: ROUTES.TOURNAMENT_SERIES, icon: <EventIcon />, label: 'Tournament Series', tone: 'info' })}
                {row({ to: ROUTES.DQ_CASES, icon: <GavelIcon />, label: 'DQ Cases', tone: 'error' })}
                {permissions.canManageJobApplications && row({ to: ROUTES.STORE_APPLICATIONS, icon: <AssignmentIcon />, label: 'Job Applications', tone: 'info' })}
                {(user?.accountType === 'store' || permissions.isAdmin) && row({ to: ROUTES.STORE_MANAGERS, icon: <PeopleIcon />, label: 'Store Managers', tone: 'secondary' })}
              </Collapse>
            </>
          )}

          {/* Admin — admin-only: archives, admin dashboard, feedback reports,
              and the stream tools. */}
          {permissions.isAdmin && (
            <>
              <Divider sx={{ my: 1, borderColor: alpha('#FFFFFF', 0.06) }} />
              {sectionHeader('Admin', adminExpanded, () => setAdminExpanded(!adminExpanded))}
              <Collapse in={adminExpanded} timeout="auto">
                {row({ to: '/archived-tournaments', icon: <ArchiveIcon />, label: 'Archived Tournaments', tone: 'warning' })}
                {row({ to: '/admin-dashboard', icon: <AdminPanelSettingsIcon />, label: 'Admin Dashboard', tone: 'error' })}
                {row({ to: ROUTES.ACCOUNTS, icon: <BadgeIcon />, label: 'Accounts', tone: 'secondary' })}
                {row({ to: ROUTES.FEEDBACK_REPORTS, icon: <FactCheckIcon />, label: 'Feedback Reports', tone: 'secondary' })}
                {row({ to: ROUTES.STREAM_MANAGER, icon: <VideocamIcon />, label: 'Stream Manager', tone: 'secondary' })}
                {row({ to: ROUTES.STREAMING_TOP_CUT, icon: <EmojiEventsIcon />, label: 'Top Cut Builder', tone: 'primary' })}
              </Collapse>
            </>
          )}
        </Box>

        {/* Pinned footer */}
        <Box sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: alpha('#FFFFFF', 0.06), py: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {footItem('Player Portal', <SwapHorizIcon />, TONE.secondary, () => { toggleDrawer(); navigate('/player'); }, true)}
          {footItem("What's New", <CampaignIcon />, TONE.info, () => { toggleDrawer(); setWhatsNewReq((n) => n + 1); })}
          {TOURS_ENABLED && footItem('Take a Tour', <ExploreIcon />, TONE.primary, () => { if (drawerOpen) toggleDrawer(); startTour(); })}
          {footItem('Send Feedback', <FeedbackIcon />, TONE.neutral, () => { toggleDrawer(); navigate(ROUTES.FEEDBACK); })}
          {footItem('Settings', <SettingsIcon />, TONE.neutral, () => { toggleDrawer(); navigate(ROUTES.USER_SETTINGS); })}
          {footItem('Log Out', <LogoutIcon />, TONE.error, () => { toggleDrawer(); handleLogout(); })}
        </Box>
      </Drawer>

      <WhatsNewDialog requestOpen={whatsNewReq} userUid={user?.uid} canManage={permissions.isAdmin} />
    </>
  );
}

export default NavMenuV2;
